import type {
  BrowserEventPayload,
  BrowserPixelAdapter,
  ScriptErrorReporter,
} from "../BrowserPixelAdapter";
import { injectVendorScript } from "../BrowserPixelAdapter";
import { buildGoogleUserData } from "../AdvancedMatching";
import type { MatchIdentity } from "../AdvancedMatching";

export { GtagAdapter, GTAG_ADAPTER_KEY, GTAG_SCRIPT };

const GTAG_ADAPTER_KEY = "gtag";
const GTAG_SCRIPT = "https://www.googletagmanager.com/gtag/js";
// Cookies en las que la etiqueta guarda el clic del anuncio: `gclid`, `wbraid`
// y `gbraid`, por ese orden (comprobado en una landing real, octubre de 2026).
// `_gcl_au` no cuenta: la tiene todo visitante que acepta las cookies.
const GTAG_CLICK_COOKIES = ["_gcl_aw", "_gcl_gb", "_gcl_ag"];

/**
 * Adaptador de la etiqueta de Google (`gtag.js`) para Google Ads.
 *
 * Lo que viaja hecho en el manifest: el `pixel_id` es el id de la etiqueta de
 * la cuenta (`AW-<id>`) y cada evento lleva en `params.send_to` la conversión
 * a disparar (`AW-<id>/<label>`), con su valor. Particularidades de la red
 * que se resuelven aquí:
 *
 * - **Instancia compartida**: si `window.gtag` ya existe (el tenant pegó su
 *   etiqueta) no se redefine. `gtag` empuja sus comandos al MISMO `dataLayer`
 *   que GTM, como hace el snippet oficial; `gtag.js` se carga una vez por
 *   página con el primer id y el resto de cuentas se añaden con `config`.
 * - **Página vista automática**: `gtag('config', 'AW-…')` la manda sola, como
 *   el Google tag. Es lo que alimenta las listas de remarketing; no hay
 *   evento de página vista en el manifest de Google.
 * - **Conversión con `transaction_id` = event_id**: la copia de servidor (Data
 *   Manager API) lleva el mismo id en la misma acción y Google se queda con una
 *   (conversiones multifuente). Un payload `custom` sería un evento con nombre
 *   propio para audiencias (`gtag('event', name, …)`); hoy el backend solo
 *   publica conversiones.
 * - **Conversiones mejoradas**: la identidad del visitante va en claro en
 *   `gtag('set', 'user_data', …)` antes de cada conversión (la etiqueta
 *   normaliza y hashea), con `allow_enhanced_conversions` en el config.
 * - **Consentimiento**: la etiqueta lee el Consent Mode de la página (el que
 *   pone el banner); aquí no se fija ningún estado por defecto. `setConsent`
 *   lo actualiza para las cuatro señales de Google.
 * - **El envío no es inmediato**: desde que se le pide una conversión hasta
 *   que la etiqueta lanza la petición pasan unos 350 ms en un ordenador y más
 *   de 1 s con la CPU de un móvil medio (medido en una landing real, octubre
 *   de 2026). Una vez lanzada sobrevive a la navegación (`keepalive`), pero si
 *   la página se va antes no sale nunca. `track` devuelve por eso una promesa
 *   que se resuelve cuando el navegador da por terminada esa petición
 *   (Resource Timing): es lo que el optin espera antes de navegar. El
 *   `event_callback` de gtag NO sirve: salta mucho antes de la petición, y
 *   también con `gtag.js` bloqueado.
 */
class GtagAdapter implements BrowserPixelAdapter {
  readonly key = GTAG_ADAPTER_KEY;
  private _loaded = false;
  private _scriptInjected = false;
  private _configured: Record<string, boolean> = {};
  private _onScriptError: ScriptErrorReporter | null;
  // Conversiones pedidas a gtag cuya petición aún no se ha visto salir
  private _awaitedHits: AwaitedHit[] = [];
  private _hitObserver: PerformanceObserver | null = null;

  constructor(onScriptError?: ScriptErrorReporter | null) {
    this._onScriptError = onScriptError ?? null;
  }

  load(): void {
    if (this._loaded) {
      return;
    }
    this._loaded = true;
    try {
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag === "function") {
        // Ya la definió el tenant: instancia compartida.
        return;
      }
      // Stub oficial: `arguments` y no un array, que es lo que procesa gtag.js
      window.gtag = function () {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      };
    } catch {
      // sin etiqueta de Google la página sigue funcionando igual
    }
  }

  /**
   * Un `config` por id de etiqueta; el script se inyecta con el primero. Con
   * identidad, `user_data` se fija antes (también en los init posteriores al
   * del optin, que llegan antes de su conversión).
   */
  init(pixelId: string, identity: MatchIdentity | null): void {
    try {
      const gtag = this._gtag();
      if (!gtag || !pixelId) {
        return;
      }
      const userData = buildGoogleUserData(identity);
      if (Object.keys(userData).length > 0) {
        gtag("set", "user_data", userData);
      }
      if (this._configured[pixelId]) {
        return;
      }
      this._configured[pixelId] = true;
      if (!this._scriptInjected) {
        this._scriptInjected = true;
        gtag("js", new Date());
        injectVendorScript(
          GTAG_SCRIPT + "?id=" + encodeURIComponent(pixelId),
          this.key,
          this._onScriptError,
        );
      }
      gtag("config", pixelId, { allow_enhanced_conversions: true });
    } catch {
      // un config fallido no puede tumbar el resto de píxeles
    }
  }

  track(
    pixelId: string,
    event: BrowserEventPayload,
    eventId: string,
  ): Promise<void> | void {
    try {
      const gtag = this._gtag();
      if (!gtag || !pixelId || !event?.name) {
        return;
      }
      const params: Record<string, unknown> = { ...(event.params ?? {}) };
      if (event.custom) {
        gtag("event", event.name, { send_to: pixelId, ...params });
        return;
      }
      const sendTo = params["send_to"];
      if (typeof sendTo !== "string" || sendTo === "") {
        return;
      }
      // El observador, antes de pedir la conversión: no se le escapa ninguna
      const sent = this._whenHitLeaves(sendTo);
      gtag("event", "conversion", { ...params, transaction_id: eventId });
      return sent;
    } catch {
      // el disparo de un píxel jamás afecta al lead ni a la navegación
    }
  }

  setConsent(granted: boolean): void {
    try {
      const state = granted ? "granted" : "denied";
      this._gtag()?.("consent", "update", {
        ad_storage: state,
        ad_user_data: state,
        ad_personalization: state,
        analytics_storage: state,
      });
    } catch {
      // sin consentimiento aplicado, la etiqueta se queda como estaba
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _gtag(): any {
    return typeof window.gtag === "function" ? window.gtag : null;
  }

  /**
   * Promesa que se resuelve cuando el navegador da por terminada una petición
   * de la conversión `AW-<id>/<label>` (`…/conversion/<id>/…label=<label>`, la
   * misma forma con y sin consentimiento). `undefined` cuando no se puede
   * saber y, por tanto, no hay nada que esperar:
   *
   * - `gtag.js` no ha cargado para esa cuenta (bloqueador, CDN caída): la
   *   conversión se queda en el `dataLayer` y no sale nunca;
   * - el visitante no ha concedido `ad_storage`: la etiqueta retiene el envío
   *   algo más de 2 s (2,3 s medidos en una landing real, frente a 350 ms con
   *   él concedido) y entonces manda solo un ping sin cookies;
   * - el navegador no tiene `PerformanceObserver`.
   *
   * Nunca rechaza y nunca caduca: quien espera pone su propio tope.
   */
  private _whenHitLeaves(sendTo: string): Promise<void> | undefined {
    try {
      const separator = sendTo.indexOf("/");
      const tagId = separator > 0 ? sendTo.slice(0, separator) : "";
      const label = separator > 0 ? sendTo.slice(separator + 1) : "";
      const accountId = tagId.replace(/^AW-/, "");
      if (!accountId || !label || !this._isLibraryLoaded(tagId)) {
        return undefined;
      }
      if (!this._isAdStorageGranted()) {
        return undefined;
      }
      if (!this._observeHits()) {
        return undefined;
      }
      return new Promise<void>((resolve) => {
        this._awaitedHits.push({
          path: "/conversion/" + accountId + "/",
          label: "label=" + encodeURIComponent(label),
          resolve,
        });
      });
    } catch {
      return undefined;
    }
  }

  // gtag.js registra cada cuenta que carga en `google_tag_manager`; con el
  // script bloqueado solo queda nuestro stub y la clave no existe.
  private _isLibraryLoaded(tagId: string): boolean {
    try {
      const registry = (
        window as unknown as { google_tag_manager?: Record<string, unknown> }
      ).google_tag_manager;
      return !!registry && !!registry[tagId];
    } catch {
      return false;
    }
  }

  // La etiqueta guarda el clic del anuncio en sus propias cookies al llegar el
  // visitante. Cubre al que no tiene el clic en el almacén del SDK (llegó por
  // otra página del dominio con la etiqueta, o se le purgó el almacenamiento).
  hasOwnClick(): boolean {
    try {
      const names = document.cookie
        .split(";")
        .map((cookie) => cookie.split("=")[0].trim());
      return GTAG_CLICK_COOKIES.some((name) => names.indexOf(name) !== -1);
    } catch {
      return false;
    }
  }

  // Estado de `ad_storage` en el Consent Mode de la página, tal como lo lleva
  // la etiqueta (`google_tag_data.ics`: no hay API pública para leerlo). El
  // `update` del banner manda sobre el `default`. Sin Consent Mode en la
  // página, o si la estructura cambiara, se da por concedido: es el caso en
  // que la etiqueta manda en el acto, y lo peor que pasa es esperar hasta el
  // tope.
  private _isAdStorageGranted(): boolean {
    try {
      const entry = (
        window as unknown as {
          google_tag_data?: {
            ics?: {
              entries?: Record<string, { default?: unknown; update?: unknown }>;
            };
          };
        }
      ).google_tag_data?.ics?.entries?.["ad_storage"];
      if (!entry) {
        return true;
      }
      if (entry.update !== undefined) {
        return entry.update === true;
      }
      return entry.default !== false;
    } catch {
      return true;
    }
  }

  private _observeHits(): boolean {
    if (this._hitObserver) {
      return true;
    }
    if (typeof PerformanceObserver !== "function") {
      return false;
    }
    const observer = new PerformanceObserver((list) => {
      try {
        for (const entry of list.getEntries()) {
          this._settleHits(entry.name);
        }
      } catch {
        // una entrada rara no puede tumbar la página
      }
    });
    try {
      observer.observe({ type: "resource" });
    } catch {
      // navegadores anteriores a la opción `type`
      observer.observe({ entryTypes: ["resource"] });
    }
    this._hitObserver = observer;
    return true;
  }

  private _settleHits(url: string): void {
    if (typeof url !== "string" || url.indexOf("/conversion/") === -1) {
      return;
    }
    const waiting: AwaitedHit[] = [];
    for (const hit of this._awaitedHits) {
      if (url.indexOf(hit.path) !== -1 && url.indexOf(hit.label) !== -1) {
        hit.resolve();
      } else {
        waiting.push(hit);
      }
    }
    this._awaitedHits = waiting;
    if (waiting.length === 0 && this._hitObserver) {
      this._hitObserver.disconnect();
      this._hitObserver = null;
    }
  }
}

interface AwaitedHit {
  // `/conversion/<id>/`: la ruta de las peticiones de conversión de la cuenta
  path: string;
  // `label=<label>`: la acción de conversión
  label: string;
  resolve: () => void;
}
